'use client';

import { useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useBoolean } from 'usehooks-ts';

import {
  ArrowDownIcon,
  Badge,
  BasketIcon,
  BellIcon,
  Button,
  CartCounter,
  CartCounterDecrement,
  CartCounterIncrement,
  CartCounterInput,
  CartCounterRemove,
  InfoIcon,
  LocaleLink,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ShoppingCartSimpleIcon,
  TypographyDeleted,
  TypographyH6,
  TypographyMuted,
  TypographyParagraph,
  TypographySmall,
  TypographyStrong,
} from '@/components';
import {
  useCartAction,
  useCartValue,
  useCheckAuthValue,
  useProductAction,
  useProductValue,
} from '@/context';
import { notifyWhenProductIsAvailable } from '@/services';
import {
  calculateDiscountPercentage,
  formatNumber,
  formatNumberWithPercentage,
  generateCartInvoice,
  generateCartProduct,
  generatePurchaseStock,
  PATHNAME,
  randomId,
  UNIT,
} from '@/utils';

const {
  LOCAL: {
    LOGIN: { BASE_URL: LOGIN_PAGE_BASE_URL },
    CART: { BASE_URL: CART_BASE_URL },
  },
  QUERY_PARAM: {
    HASH: {
      PRODUCT: { RELATED_PRODUCTS: PRODUCT_RELATED_PRODUCTS },
    },
  },
} = PATHNAME;
const { PRICE } = UNIT;

const ProductInvoiceVariables = () => {
  const {
    subProducts = [],
    variableAttributes = [],
    selectedSubProduct,
    productType,
  } = useProductValue();
  const productAction = useProductAction();

  const [convertedAttributes, setConvertedAttributes] = useState(() =>
    variableAttributes.flatMap(({ id, values, ...otherFields }) =>
      values.map((value, index) => ({
        value,
        parentId: id,
        selected: index === 0,
        ...otherFields,
      })),
    ),
  );

  useEffect(() => {
    const selectedAttributes = convertedAttributes
      .filter(({ selected }) => selected)
      .map(({ parentId, value }) => ({ parentId, value }));

    const subProduct = subProducts.find(({ attributes }) =>
      selectedAttributes.every(({ parentId, value }) => {
        const subAttr = attributes.find(({ id }) => id === parentId);
        return subAttr?.selectedValues.includes(value);
      }),
    );

    productAction({
      type: 'UPDATE_SELECTED_SUB_PRODUCT',
      payload: { selectedSubProduct: subProduct },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertedAttributes]);

  const isProductVariable = productType === 'Variable';
  const isExistedVariableAttributes = variableAttributes.length > 0;
  const subProductAbstraction = selectedSubProduct?.abstraction;

  if (isProductVariable && isExistedVariableAttributes) {
    return (
      <div>
        {variableAttributes.map(({ id, name, values }) => {
          const defaultValue = values.at(0);

          return (
            <div key={id} className='w-full space-y-2'>
              <TypographyH6 className='font-bold'>{name}</TypographyH6>

              <Select
                defaultValue={defaultValue}
                onValueChange={selectedValue => {
                  const targetAttribute = convertedAttributes.find(
                    ({ value }) => value === selectedValue,
                  );

                  setConvertedAttributes(prevAttributes =>
                    prevAttributes.map(attribute => {
                      const isSameValue =
                        attribute.value === targetAttribute?.value;
                      const isSameAttribute =
                        attribute.parentId === targetAttribute?.parentId;

                      if (isSameValue && isSameAttribute) {
                        return { ...attribute, selected: true };
                      } else if (isSameAttribute) {
                        return { ...attribute, selected: false };
                      } else {
                        return attribute;
                      }
                    }),
                  );
                }}
              >
                <SelectTrigger className='max-lg:w-60 max-sm:w-full'>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {values.map(value => {
                    const id = randomId();

                    return (
                      <SelectItem
                        key={id}
                        value={value}
                        className='min-h-10 rounded-none border-b border-border last:border-none'
                      >
                        {value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          );
        })}

        {subProductAbstraction ? (
          <div className='flex gap-2 text-muted-foreground max-lg:order-last'>
            <TypographyMuted>
              <InfoIcon className='size-4' />
            </TypographyMuted>
            <TypographyParagraph className='text-sm'>
              {subProductAbstraction}
            </TypographyParagraph>
          </div>
        ) : null}
      </div>
    );
  }
};

const ProductInvoiceAvailable = () => {
  const product = useProductValue();
  const { products } = useCartValue();
  const cartAction = useCartAction();

  const { name, packagingInfo = [], selectedSubProduct } = product;
  const mainPackagingInfo = packagingInfo.at(0);

  if (selectedSubProduct && mainPackagingInfo) {
    const { originalPrice, price = originalPrice } = selectedSubProduct;
    const { packagingModel, measurementUnit, amount } = mainPackagingInfo;

    const selectedCartProduct = products.find(
      ({ subProduct }) => subProduct.id === selectedSubProduct.id,
    );

    const { size = 0 } = selectedCartProduct ?? {};
    const isExistedSize = size > 0;

    const cartProduct = generateCartProduct(
      {
        ...product,
        subProduct: selectedSubProduct,
        packagingInfo: mainPackagingInfo,
      },
      size || 1,
    );
    const { discount, totalPrice = 0 } = generateCartInvoice([cartProduct]);

    const updateCartProductSize = (size: number) => {
      if (selectedCartProduct) {
        cartAction({
          type: 'UPDATE_CART_PRODUCT',
          payload: { ...selectedCartProduct, size },
        });
      }
    };

    return (
      <div>
        {/* top section (prices) */}
        <div className='space-y-4 *:flex *:w-full *:items-center *:justify-between *:gap-2 *:max-sm:flex-col sm:space-y-2.5'>
          <div>
            <TypographyMuted>
              <span>قیمت واحد </span>
              <span>({measurementUnit.name}):</span>
            </TypographyMuted>
            <div className='flex flex-wrap items-center justify-end gap-1'>
              {discount ? (
                <TypographyDeleted>
                  {formatNumber(originalPrice)}
                </TypographyDeleted>
              ) : null}
              <TypographyParagraph>
                <span className='font-semibold'>{formatNumber(price)}</span>
                <TypographySmall> {PRICE}</TypographySmall>
              </TypographyParagraph>
            </div>
          </div>
          <div>
            <TypographyMuted>{packagingModel.name}:</TypographyMuted>
            <TypographyParagraph>
              <span className='font-semibold'>{formatNumber(amount)}</span>
              <TypographySmall> {measurementUnit.name}</TypographySmall>
            </TypographyParagraph>
          </div>
          {discount ? (
            <div>
              <div className='flex items-center gap-1'>
                <TypographyMuted>مقدار تخفیف:</TypographyMuted>
                <Badge variant='danger' className='rounded'>
                  {formatNumberWithPercentage(
                    calculateDiscountPercentage(originalPrice, price),
                  )}
                </Badge>
              </div>
              <TypographyParagraph className='text-danger'>
                <span className='font-semibold'>{formatNumber(discount)}</span>
                <TypographySmall> {PRICE}</TypographySmall>
              </TypographyParagraph>
            </div>
          ) : null}
          <div>
            <TypographyMuted>قیمت کل:</TypographyMuted>
            <TypographyParagraph className='text-primary'>
              <TypographyStrong className='text-xl'>
                {formatNumber(totalPrice)}
              </TypographyStrong>
              <TypographySmall> {PRICE}</TypographySmall>
            </TypographyParagraph>
          </div>
        </div>

        {/* bottom section (amount) */}
        <div className='my-2.5 grid gap-4'>
          {selectedCartProduct && isExistedSize ? (
            <div className='rounded-md bg-border p-px'>
              <CartCounter {...selectedCartProduct}>
                <CartCounterIncrement
                  onClick={() => updateCartProductSize(size + 1)}
                />
                <CartCounterInput
                  onValueChange={value => updateCartProductSize(value)}
                />
                <CartCounterDecrement
                  className='disabled:hidden'
                  onClick={() => updateCartProductSize(size - 1)}
                />
                <CartCounterRemove
                  onClick={() =>
                    cartAction({
                      type: 'DELETE_CART_PRODUCT',
                      payload: selectedCartProduct,
                    })
                  }
                />
              </CartCounter>
              <div className='flex items-center justify-center gap-1 py-2'>
                <span>{formatNumber(size)}</span>
                <span className='text-sm'>{packagingModel.label}</span>
              </div>
            </div>
          ) : null}
          <Button
            asChild={isExistedSize}
            variant='default'
            className='relative flex h-12 w-full'
            onClick={() => {
              if (!isExistedSize) {
                cartAction({
                  type: 'ADD_CART_PRODUCT',
                  payload: cartProduct,
                });
                toast.success(`${name} با موفقیت به سبد خرید اضافه شد.`);
              }
            }}
          >
            {isExistedSize ? (
              <LocaleLink href={CART_BASE_URL}>
                <BasketIcon />
                <span>رفتن به سبد خرید</span>
              </LocaleLink>
            ) : (
              <>
                <ShoppingCartSimpleIcon />
                <span>افزودن به سبد خرید</span>
              </>
            )}
          </Button>
        </div>

        <Button asChild variant='link'>
          <LocaleLink href={{ hash: PRODUCT_RELATED_PRODUCTS }}>
            <span>مشاهده محصولات مرتبط</span>
            <ArrowDownIcon className='size-4' />
          </LocaleLink>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className='flex w-full items-center justify-between'>
        <TypographyMuted>وضعیت:</TypographyMuted>
        <TypographyStrong className='text-warning'>
          اطلاعات ناقص
        </TypographyStrong>
      </div>
      <TypographyParagraph className='text-center'>
        <TypographyMuted>
          متاسفانه اطلاعات کالای مورد نظر شما ناقص است، کارشناسان ما در اسرع وقت
          به این موضوع رسیدگی خواهند کرد. ممنون از صبوری شما
        </TypographyMuted>
      </TypographyParagraph>
    </div>
  );
};

const ProductInvoiceEmpty = () => {
  const { value: isNotified, setValue: setNotified } = useBoolean();
  const { id } = useProductValue();
  const { success } = useCheckAuthValue();
  const router = useRouter();

  const { mutate } = useMutation({
    mutationFn: notifyWhenProductIsAvailable,
    onSuccess: () => {
      setNotified(true);
    },
    onError: ({ message }) => {
      toast.error(message);
    },
  });

  return (
    <div>
      <div className='flex w-full items-center justify-between'>
        <TypographyMuted>وضعیت:</TypographyMuted>
        <TypographyStrong className='text-danger'>ناموجود</TypographyStrong>
      </div>
      <Button
        variant='muted'
        onClick={() => {
          if (success) {
            if (!id || !isNotified) return;
            mutate({ id });
          } else {
            toast.info('ابتدا باید وارد حساب کاربری خود شوید.');
            router.push(LOGIN_PAGE_BASE_URL);
          }
        }}
      >
        <BellIcon />
        {isNotified ? (
          <span>اطلاعیه‌ی شما با موفقیت ارسال شد</span>
        ) : (
          <span>موجود شد به من اطلاع بده</span>
        )}
      </Button>
      <TypographyParagraph className='text-center'>
        <TypographyMuted>
          متاسفانه کالای مورد نظر شما ناموجود است. در صورت تمایل میتوانید بر روی
          دکمه زیر بزنید و از موجودی بعدی این کالا با خبر شوید.
        </TypographyMuted>
      </TypographyParagraph>
    </div>
  );
};

const ProductInvoice = () => {
  const { selectedSubProduct } = useProductValue();

  let isAvailableSubProduct = true;
  if (selectedSubProduct) {
    const { stockSize } = generatePurchaseStock(selectedSubProduct);
    isAvailableSubProduct = stockSize > 0;
  }

  return (
    <div className='relative space-y-4 rounded-md border border-border p-6 *:grid *:gap-4'>
      <ProductInvoiceVariables />

      {isAvailableSubProduct ? (
        <ProductInvoiceAvailable />
      ) : (
        <ProductInvoiceEmpty />
      )}
    </div>
  );
};

export { ProductInvoice, ProductInvoiceAvailable, ProductInvoiceEmpty };
